// Video model configurations with pricing and capabilities
// Replicate costs are estimated per generation based on API pricing

export interface VideoModelConfig {
  id: string;
  displayName: string;
  tier: 'budget' | 'standard' | 'premium' | 'image-to-video';
  supportsAudio: boolean;
  optionalAudio?: boolean; // If audio can be enabled/disabled
  replicateCostUsd: {
    base: number; // Base cost for minimum config
    per5s: number; // Cost per 5 seconds
    hd1080pMultiplier: number; // Multiplier for 1080p
    perSecond?: number; // Per-second pricing for Kling models
    perSecondWithAudio?: number; // Per-second with audio
  };
  durations: number[];
  qualities: string[];
  aspectRatios: string[];
  maxDuration: number;
  supports: {
    startImage: boolean;
    lastFrameImage: boolean;
    negativePrompt: boolean;
    styles: boolean;
    effects: boolean;
    seed: boolean;
    motionVideo?: boolean; // For animation transfer
    referenceImages?: boolean;
    cameraFixed?: boolean; // For models that support fixed camera
    qualityMode?: boolean; // For models with standard/high quality mode
    multipleReferenceImages?: boolean; // For models supporting multiple reference images
    durationFromInput?: boolean; // For models where output duration matches input video
    aspectRatioFromImage?: boolean; // For models where aspect ratio comes from input image
  };
  limitations: string[];
}

// PixVerse v5 unit-based pricing lookup (Replicate cost in USD)
const PIXVERSE_COSTS: Record<string, number> = {
  '5s-360p-normal': 0.30,
  '5s-360p-smooth': 0.60,
  '8s-360p-normal': 0.60,
  '5s-540p-normal': 0.30,
  '5s-540p-smooth': 0.60,
  '8s-540p-normal': 0.60,
  '5s-720p-normal': 0.40,
  '5s-720p-smooth': 0.80,
  '8s-720p-normal': 0.80,
  '5s-1080p-normal': 0.80,
  '8s-1080p-normal': 1.60,
};

// Hailuo 02 unit-based pricing lookup (Replicate cost in USD)
const HAILUO_COSTS: Record<string, number> = {
  '6s-512p': 0.10,
  '10s-512p': 0.15,
  '6s-768p': 0.27,
  '10s-768p': 0.45,
  '6s-1080p': 0.48,
};

export const VIDEO_MODELS: Record<string, VideoModelConfig> = {
  'pixverse-v5': {
    id: 'pixverse/pixverse-v5',
    displayName: 'PixVerse v5',
    tier: 'standard',
    supportsAudio: false,
    replicateCostUsd: {
      base: 0.30, // Fallback, actual cost from lookup
      per5s: 0.15,
      hd1080pMultiplier: 1.8,
    },
    durations: [5, 8],
    qualities: ['360p', '540p', '720p', '1080p'],
    aspectRatios: ['16:9', '9:16', '1:1'],
    maxDuration: 8,
    supports: {
      startImage: true,
      lastFrameImage: true,
      negativePrompt: true,
      styles: true,
      effects: true,
      seed: true,
    },
    limitations: [
      '1080p not available with smooth motion',
      'Smooth motion only available for 5s videos',
      'Effects cannot be used with last frame image',
    ],
  },
  'kling-v2.6': {
    id: 'kwaivgi/kling-v2.6',
    displayName: 'Kling 2.6',
    tier: 'standard',
    supportsAudio: true,
    optionalAudio: true,
    replicateCostUsd: {
      base: 0,
      per5s: 0.35, // $0.07/sec * 5 = $0.35
      hd1080pMultiplier: 1.0,
      perSecond: 0.07,
      perSecondWithAudio: 0.14,
    },
    durations: [5, 8, 10],
    qualities: ['720p', '1080p'],
    aspectRatios: ['16:9', '9:16', '1:1'],
    maxDuration: 10,
    supports: {
      startImage: true,
      lastFrameImage: false,
      negativePrompt: true,
      styles: false,
      effects: false,
      seed: true,
    },
    limitations: [],
  },
  'sora-2': {
    id: 'openai/sora-2',
    displayName: 'Sora 2',
    tier: 'premium',
    supportsAudio: true, // Always has audio
    optionalAudio: false,
    replicateCostUsd: {
      base: 0,
      per5s: 0.50,
      hd1080pMultiplier: 1.0,
      perSecond: 0.10,
    },
    durations: [4, 8, 12],
    qualities: [],  // Fixed: Sora 2 does not support resolution selection
    aspectRatios: ['16:9', '9:16'], // Landscape or portrait only (maps to 'landscape'/'portrait')
    maxDuration: 12,
    supports: {
      startImage: true,
      lastFrameImage: false,
      negativePrompt: false,  // Fixed: not supported
      styles: false,
      effects: false,
      seed: false,  // Fixed: not supported
    },
    limitations: ['Resolution is fixed at 720p (landscape) or 1280x720 (portrait)'],
  },
  'sora-2-pro': {
    id: 'openai/sora-2-pro',
    displayName: 'Sora 2 Pro',
    tier: 'premium',
    supportsAudio: true,
    optionalAudio: false,
    replicateCostUsd: {
      base: 0,
      per5s: 1.50,
      hd1080pMultiplier: 1.0,
      perSecond: 0.30, // 720p = standard ($0.30/sec), 1080p = high ($0.50/sec)
    },
    durations: [4, 8, 12],
    qualities: ['720p', '1080p'], // 720p = standard mode, 1080p = high mode (sent to API)
    aspectRatios: ['16:9', '9:16'],
    maxDuration: 12,
    supports: {
      startImage: true,
      lastFrameImage: false,
      negativePrompt: true,
      styles: false,
      effects: false,
      seed: true,
    },
    limitations: ['720p uses Standard quality, 1080p uses High quality mode'],
  },
  'veo-3.1-fast': {
    id: 'google/veo-3.1-fast',
    displayName: 'Veo 3.1 Fast',
    tier: 'premium',
    supportsAudio: true,
    optionalAudio: true,
    replicateCostUsd: {
      base: 0,
      per5s: 0.50,  // $0.10/sec * 5s
      hd1080pMultiplier: 1.0,
      perSecond: 0.10,          // $0.10/sec without audio
      perSecondWithAudio: 0.15, // $0.15/sec with audio
    },
    durations: [4, 6, 8],
    qualities: ['720p', '1080p'],
    aspectRatios: ['16:9', '9:16'],
    maxDuration: 8,
    supports: {
      startImage: true,
      lastFrameImage: true,
      negativePrompt: false,
      styles: false,
      effects: false,
      seed: true,
      referenceImages: true,
    },
    limitations: [],
  },
  'veo-3.1': {
    id: 'google/veo-3.1',
    displayName: 'Veo 3.1',
    tier: 'premium',
    supportsAudio: true,
    optionalAudio: true,
    replicateCostUsd: {
      base: 0,
      per5s: 1.00,
      hd1080pMultiplier: 1.0,
      perSecond: 0.20,
      perSecondWithAudio: 0.40,
    },
    durations: [4, 6, 8],
    qualities: ['720p', '1080p'],
    aspectRatios: ['16:9', '9:16', '1:1'],
    maxDuration: 8,
    supports: {
      startImage: true,
      lastFrameImage: true,
      negativePrompt: false,
      styles: false,
      effects: false,
      seed: true,
      referenceImages: true,
      multipleReferenceImages: true, // Up to 4 reference images
    },
    limitations: [],
  },
  'kling-motion-control': {
    id: 'kwaivgi/kling-v2.6-motion-control',
    displayName: 'Kling Motion Control',
    tier: 'image-to-video',
    supportsAudio: false,
    replicateCostUsd: {
      base: 0,
      per5s: 0.35, // $0.07/sec * 5 = $0.35 (standard mode)
      hd1080pMultiplier: 1.0,
      perSecond: 0.07, // Standard mode: $0.07/sec
      perSecondWithAudio: 0.12, // Pro mode: $0.12/sec (reusing this field for pro)
    },
    durations: [], // Duration determined by input video length (max 30s)
    qualities: ['standard', 'pro'], // Mode selection: Standard ($0.07/s) or Pro ($0.12/s)
    aspectRatios: [], // Aspect ratio determined by character image
    maxDuration: 30, // Max 30s based on input video
    supports: {
      startImage: true, // Required: character image
      lastFrameImage: false,
      negativePrompt: false, // Not supported
      styles: false,
      effects: false,
      seed: false, // Not supported
      motionVideo: true, // Required: motion reference video
      durationFromInput: true, // Duration comes from motion video
      aspectRatioFromImage: true, // Aspect ratio comes from character image
    },
    limitations: ['Output duration matches input motion video (max 30s)', 'Aspect ratio matches character image'],
  },
  'seedance-1.5-pro': {
    id: 'bytedance/seedance-1.5-pro',
    displayName: 'Seedance 1.5 Pro',
    tier: 'budget',
    supportsAudio: true,
    optionalAudio: true,
    replicateCostUsd: {
      base: 0,
      per5s: 0.13,  // $0.026/sec * 5 = $0.13 (without audio)
      hd1080pMultiplier: 1.0,
      perSecond: 0.026,  // Without audio
      perSecondWithAudio: 0.052,  // With audio
    },
    durations: [5, 10],  // Fixed: only 5 and 10 seconds verified to work
    qualities: ['720p'], // Fixed resolution
    aspectRatios: ['16:9', '9:16', '1:1', '4:3', '3:4', '21:9', '9:21'],
    maxDuration: 12,
    supports: {
      startImage: true,
      lastFrameImage: true,
      negativePrompt: false,
      styles: false,
      effects: false,
      seed: true,
      cameraFixed: true,
    },
    limitations: ['Last frame image requires start image'],
  },
  'seedance-1-pro-fast': {
    id: 'bytedance/seedance-1-pro-fast',
    displayName: 'Seedance 1 Pro Fast',
    tier: 'budget',
    supportsAudio: false,
    optionalAudio: false,
    replicateCostUsd: {
      base: 0,
      per5s: 0.075, // Fallback, actual cost from lookup
      hd1080pMultiplier: 1.0,
      perSecond: 0.015, // Base rate for 480p
    },
    durations: [5, 8, 10, 12],  // Fixed: API supports up to 12s
    qualities: ['480p', '720p', '1080p'], // Resolution-based pricing
    aspectRatios: ['16:9', '9:16', '1:1'],
    maxDuration: 12,  // Fixed: was 10
    supports: {
      startImage: true,
      lastFrameImage: false,
      negativePrompt: false,
      styles: false,
      effects: false,
      seed: true,
      // Note: camera_fixed not supported on this model
    },
    limitations: [],
  },
  'hailuo-02': {
    id: 'minimax/hailuo-02',
    displayName: 'Hailuo 02',
    tier: 'budget',
    supportsAudio: false,
    optionalAudio: false,
    replicateCostUsd: {
      base: 0.10,
      per5s: 0.08,
      hd1080pMultiplier: 2.0,
    },
    durations: [6, 10],
    qualities: ['512p', '768p', '1080p'],
    aspectRatios: ['16:9', '9:16', '1:1'],  // Note: derived from first_frame_image
    maxDuration: 10,
    supports: {
      startImage: true,
      lastFrameImage: true,  // Fixed: Hailuo 02 supports last_frame_image
      negativePrompt: false,
      styles: false,
      effects: false,
      seed: false,  // Fixed: seed not in API schema
    },
    limitations: ['1080p only available for 6s duration', 'Aspect ratio derived from first frame image'],
  },
  'wan-super': {
    id: 'wan-video/wan-2.2-t2v-fast', // Default to t2v, edge function switches based on image
    displayName: 'Wan Super',
    tier: 'budget',
    supportsAudio: false,
    optionalAudio: false,
    replicateCostUsd: {
      base: 0.05, // Fixed $0.05 cost
      per5s: 0,
      hd1080pMultiplier: 1.0,
    },
    durations: [5], // Fixed 5 seconds only
    qualities: ['480p'], // 720p excluded (doubles cost)
    aspectRatios: ['16:9', '9:16', '1:1'],
    maxDuration: 5,
    supports: {
      startImage: true, // Supports image input (switches to i2v model)
      lastFrameImage: false,
      negativePrompt: true,
      styles: false,
      effects: false,
      seed: true,
    },
    limitations: ['Fixed 5 second duration'],
  },
};

// Credit cost calculation with tiered margins
export interface VideoCostBreakdown {
  model: string;
  modelName: string;
  duration: number;
  quality: string;
  motionMode?: string;
  enableAudio?: boolean;
  replicateCostUsd: number;
  creditCost: number;
  userPaysCents: number;
  marginPercent: number;
}

// Pricing constants
const CREDIT_VALUE_USD = 0.0002; // 1 credit = $0.0002
const TARGET_MARGIN = 0.67;      // 67% margin for budget (200% markup)
const MEDIUM_MARGIN = 0.60;      // 60% margin for medium (150% markup)
const PREMIUM_MARGIN = 0.40;     // 40% margin for premium (67% markup)

// Model tier configuration with margins
const MODEL_MARGINS: Record<string, number> = {
  'seedance-1.5-pro': TARGET_MARGIN,
  'seedance-1-pro-fast': TARGET_MARGIN,
  'wan-super': 0.50,
  'hailuo-02': MEDIUM_MARGIN,
  'pixverse-v5': MEDIUM_MARGIN,
  'kling-v2.6': MEDIUM_MARGIN,
  'sora-2': PREMIUM_MARGIN,
  'sora-2-pro': PREMIUM_MARGIN,
  'veo-3.1-fast': TARGET_MARGIN,  // 67% margin
  'veo-3.1': TARGET_MARGIN,        // 67% margin
  'kling-motion-control': 0.50, // 50% margin = 100% markup
};

// Get PixVerse v5 cost from lookup table
function getPixVerseCost(duration: number, quality: string, motionMode: string): number {
  const key = `${duration}s-${quality}-${motionMode}`;
  return PIXVERSE_COSTS[key] || 1.60;
}

// Get Hailuo 02 cost from lookup table
function getHailuoCost(duration: number, quality: string): number {
  const key = `${duration}s-${quality}`;
  return HAILUO_COSTS[key] || 0.48;
}

// Helper to calculate credits and build result
function buildCostResult(
  modelId: string,
  modelName: string,
  duration: number,
  quality: string,
  motionMode: string,
  enableAudio: boolean,
  replicateCost: number,
  margin: number
): VideoCostBreakdown {
  const userPaysUsd = replicateCost / (1 - margin);
  let creditCost = Math.ceil(userPaysUsd / CREDIT_VALUE_USD);
  creditCost = Math.max(creditCost, 500);
  
  const actualUserPaysUsd = creditCost * CREDIT_VALUE_USD;
  const marginPercent = ((actualUserPaysUsd - replicateCost) / actualUserPaysUsd) * 100;

  return {
    model: modelId,
    modelName,
    duration,
    quality,
    motionMode,
    enableAudio,
    replicateCostUsd: parseFloat(replicateCost.toFixed(4)),
    creditCost,
    userPaysCents: parseFloat((actualUserPaysUsd * 100).toFixed(2)),
    marginPercent: parseFloat(marginPercent.toFixed(1)),
  };
}

export function calculateVideoCost(
  modelId: string,
  duration: number,
  quality: string,
  motionMode: string = 'normal',
  enableAudio: boolean = false,
  motionVideoDuration?: number // Actual duration of uploaded motion video for Kling Motion Control
): VideoCostBreakdown {
  const model = VIDEO_MODELS[modelId];
  if (!model) {
    return calculateVideoCost('pixverse-v5', duration, quality, motionMode, enableAudio);
  }

  const margin = MODEL_MARGINS[modelId] ?? TARGET_MARGIN;

  // Veo 3.1 Fast: Per-second with optional audio ($0.10/$0.15)
  if (modelId === 'veo-3.1-fast') {
    const perSecond = enableAudio ? 0.15 : 0.10;
    const replicateCost = perSecond * duration;
    return buildCostResult(modelId, model.displayName, duration, quality, motionMode, enableAudio, replicateCost, margin);
  }

  // Veo 3.1: Per-second with optional audio ($0.20/$0.40)
  if (modelId === 'veo-3.1') {
    const perSecond = enableAudio ? 0.40 : 0.20;
    const replicateCost = perSecond * duration;
    return buildCostResult(modelId, model.displayName, duration, quality, motionMode, enableAudio, replicateCost, margin);
  }

  // PixVerse v5: Unit-based lookup
  if (modelId === 'pixverse-v5') {
    const replicateCost = getPixVerseCost(duration, quality, motionMode);
    return buildCostResult(modelId, model.displayName, duration, quality, motionMode, enableAudio, replicateCost, margin);
  }

  // Kling v2.6: Per-second pricing
  if (modelId === 'kling-v2.6') {
    const perSecond = enableAudio && model.replicateCostUsd.perSecondWithAudio 
      ? model.replicateCostUsd.perSecondWithAudio 
      : model.replicateCostUsd.perSecond || 0.07;
    const replicateCost = perSecond * duration;
    return buildCostResult(modelId, model.displayName, duration, quality, motionMode, enableAudio, replicateCost, margin);
  }

  // Sora 2: Per-second pricing (always with audio)
  if (modelId === 'sora-2') {
    const replicateCost = 0.10 * duration;
    return buildCostResult(modelId, model.displayName, duration, quality, motionMode, true, replicateCost, margin);
  }

  // Sora 2 Pro: 720p = standard ($0.30/sec), 1080p = high ($0.50/sec)
  if (modelId === 'sora-2-pro') {
    const perSecond = quality === '1080p' ? 0.50 : 0.30;
    const replicateCost = perSecond * duration;
    return buildCostResult(modelId, model.displayName, duration, quality, motionMode, true, replicateCost, margin);
  }

  // Hailuo 02: Unit-based lookup
  if (modelId === 'hailuo-02') {
    const replicateCost = getHailuoCost(duration, quality);
    return buildCostResult(modelId, model.displayName, duration, quality, motionMode, enableAudio, replicateCost, margin);
  }

  // Wan Super: Fixed 500 credits
  if (modelId === 'wan-super') {
    return {
      model: modelId,
      modelName: model.displayName,
      duration: 5,
      quality,
      motionMode,
      enableAudio: false,
      replicateCostUsd: 0.05,
      creditCost: 500,
      userPaysCents: 0.10,
      marginPercent: 50,
    };
  }

  // Seedance 1 Pro Fast: Resolution-based pricing
  if (modelId === 'seedance-1-pro-fast') {
    const rateByQuality: Record<string, number> = { '480p': 0.015, '720p': 0.025, '1080p': 0.06 };
    const perSecond = rateByQuality[quality] || 0.015;
    const replicateCost = perSecond * duration;
    return buildCostResult(modelId, model.displayName, duration, quality, motionMode, enableAudio, replicateCost, margin);
  }

  // Seedance 1.5 Pro: Per-second with optional audio
  if (modelId === 'seedance-1.5-pro') {
    const perSecond = enableAudio ? 0.052 : 0.026;
    const replicateCost = perSecond * duration;
    return buildCostResult(modelId, model.displayName, duration, quality, motionMode, enableAudio, replicateCost, margin);
  }

  // Kling Motion Control: Per-second with mode (standard/pro), duration from uploaded video
  if (modelId === 'kling-motion-control') {
    // Use actual motion video duration if available, otherwise fallback to duration
    const actualDuration = motionVideoDuration || duration || 5;
    const perSecond = quality === 'pro' ? 0.12 : 0.07;
    const replicateCost = perSecond * actualDuration;
    return buildCostResult(modelId, model.displayName, actualDuration, quality, motionMode, enableAudio, replicateCost, margin);
  }

  // Fallback: Generic calculation
  let replicateCost = model.replicateCostUsd.base;
  replicateCost += model.replicateCostUsd.per5s * (duration / 5);
  if (quality === '1080p') replicateCost *= model.replicateCostUsd.hd1080pMultiplier;
  if (enableAudio && model.optionalAudio) replicateCost *= 2.0;

  return buildCostResult(modelId, model.displayName, duration, quality, motionMode, enableAudio, replicateCost, margin);
}

// Pre-calculated costs for common configurations (for admin dashboard)
export function getAllModelCosts(): VideoCostBreakdown[] {
  const costs: VideoCostBreakdown[] = [];
  
  // PixVerse v5 configurations
  costs.push(calculateVideoCost('pixverse-v5', 5, '720p', 'normal'));
  costs.push(calculateVideoCost('pixverse-v5', 5, '1080p', 'normal'));
  costs.push(calculateVideoCost('pixverse-v5', 8, '720p', 'normal'));
  costs.push(calculateVideoCost('pixverse-v5', 8, '1080p', 'normal'));
  costs.push(calculateVideoCost('pixverse-v5', 5, '720p', 'smooth'));
  
  // Kling 1.6 Standard configurations
  costs.push(calculateVideoCost('kling-v1.6-standard', 5, '720p'));
  costs.push(calculateVideoCost('kling-v1.6-standard', 8, '720p'));
  costs.push(calculateVideoCost('kling-v1.6-standard', 10, '1080p'));
  
  // Kling 2.6 configurations
  costs.push(calculateVideoCost('kling-v2.6', 5, '720p', 'normal', false));
  costs.push(calculateVideoCost('kling-v2.6', 5, '720p', 'normal', true)); // with audio
  costs.push(calculateVideoCost('kling-v2.6', 8, '720p', 'normal', false));
  costs.push(calculateVideoCost('kling-v2.6', 8, '720p', 'normal', true)); // with audio
  
  // Sora 2 configurations
  costs.push(calculateVideoCost('sora-2', 4, '720p'));
  costs.push(calculateVideoCost('sora-2', 8, '1080p'));
  costs.push(calculateVideoCost('sora-2', 12, '1080p'));
  
  // Veo 3.1 Fast configurations
  costs.push(calculateVideoCost('veo-3.1-fast', 4, '720p'));
  costs.push(calculateVideoCost('veo-3.1-fast', 8, '1080p'));
  
  // Kling Motion Control configurations
  costs.push(calculateVideoCost('kling-motion-control', 5, 'standard'));
  costs.push(calculateVideoCost('kling-motion-control', 10, 'standard'));
  costs.push(calculateVideoCost('kling-motion-control', 5, 'pro'));
  costs.push(calculateVideoCost('kling-motion-control', 10, 'pro'));
  
  // Seedance 1.5 Pro configurations
  costs.push(calculateVideoCost('seedance-1.5-pro', 5, '720p'));
  costs.push(calculateVideoCost('seedance-1.5-pro', 8, '1080p'));
  costs.push(calculateVideoCost('seedance-1.5-pro', 10, '1080p'));
  costs.push(calculateVideoCost('seedance-1.5-pro', 5, '720p', 'normal', true)); // with audio
  costs.push(calculateVideoCost('seedance-1.5-pro', 10, '1080p', 'normal', true)); // with audio
  
  // Seedance 1 Pro Fast configurations (cheapest)
  costs.push(calculateVideoCost('seedance-1-pro-fast', 5, '720p'));
  costs.push(calculateVideoCost('seedance-1-pro-fast', 8, '720p'));
  costs.push(calculateVideoCost('seedance-1-pro-fast', 10, '1080p'));
  
  // Hailuo 02 configurations
  costs.push(calculateVideoCost('hailuo-02', 6, '512p'));
  costs.push(calculateVideoCost('hailuo-02', 6, '768p'));
  costs.push(calculateVideoCost('hailuo-02', 6, '1080p'));
  costs.push(calculateVideoCost('hailuo-02', 10, '768p'));
  
  return costs;
}

// Image model costs for admin dashboard (100x scaled, 3x markup)
export const IMAGE_MODEL_COSTS = [
  { model: 'Leonardo Lucid', replicateCostUsd: 0.00225, creditCost: 100, marginPercent: 88.75 },
  { model: 'Flux Schnell', replicateCostUsd: 0.003, creditCost: 50, marginPercent: 70 },
  { model: 'MiniMax Image', replicateCostUsd: 0.01, creditCost: 150, marginPercent: 67 },
  { model: 'Nano Banana', replicateCostUsd: 0.039, creditCost: 600, marginPercent: 67 },
];

// Music model costs for admin dashboard (100x scaled, 3x markup)
export const MUSIC_MODEL_COSTS = [
  { model: 'Suno (per track)', replicateCostUsd: 0.30, creditCost: 4500, marginPercent: 67 },
];

export function getModelById(modelId: string): VideoModelConfig | undefined {
  return VIDEO_MODELS[modelId];
}

export function getDefaultModel(): string {
  return 'pixverse-v5';
}
